import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { useInactivityLogout } from '../auth/useInactivityLogout.js';
import GlobalSearch from '../components/GlobalSearch.jsx';
import InactivityWarningModal from '../components/InactivityWarningModal.jsx';
import UpdateGateModal from '../components/UpdateGateModal.jsx';
import { useAktivitaetChangeListener } from '../components/AktivitaetModalProvider.jsx';
import { pb } from '../lib/pocketbase.js';
import { bestaetigeUpdate, fetchUnbestaetigteUpdates } from '../lib/updates.js';
import logo from '../assets/logo.png';

const baseNavigation = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/praxen', label: 'Praxen & Kliniken' },
  { to: '/selektionen', label: 'Selektionen' },
  { to: '/neuigkeiten', label: 'Neuigkeiten' },
];

export default function AppShell() {
  const { user, isAuthenticated, isAdmin, isFuehrungskraftOrAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  // Auto-Logout nach 30 Min Inaktivität, mit 2-Min-Warnung.
  // Läuft nur, solange ein User eingeloggt ist.
  const { warningOpen, secondsLeft, dismissWarning, logoutNow } = useInactivityLogout({
    enabled: isAuthenticated,
    onLogout: () => navigate('/login', { replace: true }),
  });

  // Stapel unbestätigter Updates für das Login-Gate. Wird einmal pro
  // AppShell-Mount geladen (= einmal pro Login bzw. Hard-Reload).
  const [unbestaetigte, setUnbestaetigte] = useState([]);

  // Team-weite offene Aufgaben — als Badge am Team-Menüpunkt.
  // Nur für Führungskraft/Admin geladen, wird auf Save-Events neu gezählt.
  const [teamOffenCount, setTeamOffenCount] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    fetchUnbestaetigteUpdates(user.id)
      .then((list) => {
        if (!cancelled) setUnbestaetigte(list);
      })
      .catch(() => {
        // Fehler beim Laden des Changelogs blockiert den Login nicht.
        if (!cancelled) setUnbestaetigte([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Team-Badge: Anzahl ALLER offenen Aktivitäten (status != erledigt).
  // getList(1, 1) reicht — wir brauchen nur totalItems, nicht die Records.
  useEffect(() => {
    if (!isFuehrungskraftOrAdmin) return undefined;
    let cancelled = false;
    async function loadCount() {
      try {
        const res = await pb.collection('aktivitaeten').getList(1, 1, {
          filter: 'status != "erledigt"',
          fields: 'id',
        });
        if (!cancelled) setTeamOffenCount(res.totalItems);
      } catch {
        if (!cancelled) setTeamOffenCount(0);
      }
    }
    loadCount();
    return () => {
      cancelled = true;
    };
  }, [isFuehrungskraftOrAdmin]);

  // Wenn irgendwo eine Aktivität gespeichert wird, Badge neu zählen —
  // damit der Wert nicht stale wird, sobald jemand was abhakt.
  useAktivitaetChangeListener(async () => {
    if (!isFuehrungskraftOrAdmin) return;
    try {
      const res = await pb.collection('aktivitaeten').getList(1, 1, {
        filter: 'status != "erledigt"',
        fields: 'id',
      });
      setTeamOffenCount(res.totalItems);
    } catch {
      // ignorieren — Badge bleibt mit altem Wert stehen
    }
  });

  async function handleConfirmUpdate(update) {
    try {
      await bestaetigeUpdate(update.id, user.id);
    } catch (err) {
      // Falls das Speichern scheitert, trotzdem weitermachen — der User
      // sieht das Update beim nächsten Login wieder. Kein harter Block.
      console.error('Bestätigung konnte nicht gespeichert werden', err);
    }
    setUnbestaetigte((prev) => prev.filter((u) => u.id !== update.id));
  }

  const navigation = [...baseNavigation];
  if (isFuehrungskraftOrAdmin) {
    navigation.push(
      { to: '/partner', label: 'Partner' },
      { to: '/auswertung', label: 'Auswertung' },
      {
        to: '/team',
        label: 'Team',
        icon: '👥',
        badge: teamOffenCount > 0 ? teamOffenCount : null,
      }
    );
  }
  if (isAdmin) {
    navigation.push(
      { to: '/admin', label: 'Admin' },
      { to: '/admin/updates', label: 'Updates verwalten' },
      { to: '/admin/import', label: 'Import' }
    );
  }

  const aktivesUpdate = unbestaetigte[0] ?? null;

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="hidden w-64 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="px-6 py-5">
          <Link to="/" className="inline-block" aria-label="Zum Dashboard">
            <img src={logo} alt="medifin" className="h-auto w-[180px]" />
          </Link>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? 'bg-primary-100 font-semibold text-primary-800'
                    : 'text-primary-700 hover:bg-primary-50 hover:text-primary-800'
                }`
              }
            >
              <span className="inline-flex items-center gap-2">
                {item.icon && <span aria-hidden="true">{item.icon}</span>}
                {item.label}
              </span>
              {item.badge != null && (
                <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold text-white">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 px-6 py-4 text-xs text-primary-600">
          eingeloggt als
          <div className="truncate text-sm font-medium text-primary-700">
            {user?.email ?? 'unbekannt'}
          </div>
        </div>
      </aside>

      {/* Hauptbereich */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center gap-4 border-b border-slate-200 bg-white px-6 py-3">
          <div className="md:hidden">
            <Link to="/" aria-label="Zum Dashboard">
              <img src={logo} alt="medifin" className="h-auto w-[140px]" />
            </Link>
          </div>
          <div className="max-w-xl flex-1">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-600 sm:inline">{user?.email}</span>
            <button type="button" onClick={handleLogout} className="btn-secondary">
              Abmelden
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-slate-50 p-6">
          <Outlet />
        </main>
      </div>

      {aktivesUpdate && (
        <UpdateGateModal
          key={aktivesUpdate.id}
          update={aktivesUpdate}
          verbleibend={unbestaetigte.length}
          onConfirm={handleConfirmUpdate}
        />
      )}

      {warningOpen && (
        <InactivityWarningModal
          secondsLeft={secondsLeft}
          onStay={dismissWarning}
          onLogout={logoutNow}
        />
      )}
    </div>
  );
}
